/**
 * Chrome's async clipboard accepts only `text/plain`, `text/html`, `image/png` and
 * `image/svg+xml` on write, and rejects the *entire* `ClipboardItem` if any entry is an
 * unsupported type. The source image can be served as webp or jpeg, so re-encode anything
 * that is not already PNG before writing it to the clipboard.
 */
const toPngBlob = async (blob: Blob): Promise<Blob> => {
	if (blob.type === 'image/png') {
		return blob;
	}

	const bitmap = await createImageBitmap(blob);
	try {
		const canvas = document.createElement('canvas');
		canvas.width = bitmap.width;
		canvas.height = bitmap.height;

		const context = canvas.getContext('2d');
		if (!context) {
			throw new Error('Could not acquire a 2d canvas context to re-encode the image as PNG');
		}
		context.drawImage(bitmap, 0, 0);

		return await new Promise<Blob>((resolve, reject) => {
			canvas.toBlob(
				(png) => (png ? resolve(png) : reject(new Error('Canvas produced no PNG blob'))),
				'image/png',
			);
		});
	} finally {
		bitmap.close();
	}
};

/**
 * Copies the image at `imageUrl` to the clipboard as PNG bytes.
 *
 * Fetches the image, re-encodes it to PNG if needed (see {@link toPngBlob}), and writes it to the
 * clipboard as a single `image/png` `ClipboardItem`.
 *
 * @throws If the image fetch fails, so callers can surface an error state to the user. Without this
 * guard an error/HTML response body would be written to the clipboard as if it were an image.
 */
export async function copyImageToClipboard(imageUrl: string): Promise<void> {
	const response = await fetch(imageUrl);
	if (!response.ok) {
		throw new Error(`Failed to fetch image for copying, status ${response.status}`);
	}

	const blob = await response.blob();
	const png = await toPngBlob(blob);

	await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
}
